
(() => {
  const DAY_KEYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
  const EN_WEEKDAY_INDEX = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };
  const TXT = {
    title: "R\u00e9server maintenant",
    pickDate: "Recherche des disponibilit\u00e9s...",
    pickSlot: "S\u00e9lectionnez un cr\u00e9neau ci-dessous",
    cta: "R\u00e9server maintenant",
    ctaAfter: "Valider",
    ctaLoading: "Validation en cours...",
    added: "Le rendez-vous a \u00e9t\u00e9 ajout\u00e9 au panier.",
    lockedNotice: "Il vous est r\u00e9serv\u00e9 pendant {minutes} minutes jusqu'au paiement.",
    checkoutAction: "Aller au panier",
    ctaError: "Une erreur est survenue.",
    noSlot: "Aucun cr\u00e9neau disponible."
  };

  const fmtCache = new Map();

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
      return;
    }
    fn();
  }

  function toInt(v, d = 0) {
    const n = Number.parseInt(String(v ?? ""), 10);
    return Number.isFinite(n) ? n : d;
  }

  function toBool(v, d = false) {
    if (v === undefined || v === null || v === "") return d;
    const x = String(v).toLowerCase().trim();
    if (["1", "true", "yes", "oui", "on"].includes(x)) return true;
    if (["0", "false", "no", "non", "off"].includes(x)) return false;
    return d;
  }

  function cap(s) {
    const x = String(s || "");
    return x ? x.charAt(0).toUpperCase() + x.slice(1) : "";
  }

  function pad2(n) {
    return String(n).padStart(2, "0");
  }

  function norm(s) {
    return String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  }

  function fmt(locale, timeZone, options) {
    const key = `${locale}|${timeZone}|${JSON.stringify(options)}`;
    if (!fmtCache.has(key)) {
      fmtCache.set(key, new Intl.DateTimeFormat(locale, { ...options, timeZone }));
    }
    return fmtCache.get(key);
  }

  function parseDateKey(k) {
    const m = String(k || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
  }

  function dateKey(y, m, d) {
    return `${y}-${pad2(m)}-${pad2(d)}`;
  }

  function cmpDateKey(a, b) {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  }

  function addDays(k, delta) {
    const p = parseDateKey(k);
    if (!p) return k;
    const ref = new Date(Date.UTC(p.y, p.m - 1, p.d, 12, 0, 0, 0));
    ref.setUTCDate(ref.getUTCDate() + delta);
    return dateKey(ref.getUTCFullYear(), ref.getUTCMonth() + 1, ref.getUTCDate());
  }

  function tzParts(date, timeZone) {
    const f = fmt("en-US", timeZone, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      weekday: "long",
      hourCycle: "h23"
    });
    const out = {};
    f.formatToParts(date).forEach((p) => {
      if (p.type !== "literal") out[p.type] = p.value;
    });
    return {
      y: Number(out.year),
      m: Number(out.month),
      d: Number(out.day),
      h: Number(out.hour),
      min: Number(out.minute),
      s: Number(out.second),
      wd: norm(out.weekday)
    };
  }

  function tzOffsetMs(date, timeZone) {
    const p = tzParts(date, timeZone);
    const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s, 0);
    return asUtc - date.getTime();
  }

  function dateKeyNow(timeZone) {
    const p = tzParts(new Date(), timeZone);
    return dateKey(p.y, p.m, p.d);
  }

  function tzDateTimeToUtcMs(dateK, hhmm, timeZone) {
    const d = parseDateKey(dateK);
    const t = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
    if (!d || !t) return Number.NaN;
    const hh = Number(t[1]);
    const mm = Number(t[2]);
    const base = Date.UTC(d.y, d.m - 1, d.d, hh, mm, 0, 0);
    let guess = base;
    for (let i = 0; i < 4; i += 1) {
      const off = tzOffsetMs(new Date(guess), timeZone);
      const target = base - off;
      if (Math.abs(target - guess) < 1000) {
        guess = target;
        break;
      }
      guess = target;
    }
    return guess;
  }

  function weekdayIndex(dateK, timeZone) {
    const p = parseDateKey(dateK);
    if (!p) return 0;
    const ref = new Date(Date.UTC(p.y, p.m - 1, p.d, 12, 0, 0, 0));
    const wd = tzParts(ref, timeZone).wd;
    return wd in EN_WEEKDAY_INDEX ? EN_WEEKDAY_INDEX[wd] : ref.getUTCDay();
  }

  function dayKeyForDate(dateK, timeZone) {
    return DAY_KEYS[weekdayIndex(dateK, timeZone)] || "";
  }

  function formatDateLong(dateK, locale, timeZone) {
    const p = parseDateKey(dateK);
    if (!p) return dateK;
    const d = new Date(Date.UTC(p.y, p.m - 1, p.d, 12, 0, 0, 0));
    const f = fmt(locale, timeZone, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return cap(f.format(d));
  }

  function formatTime(iso, locale, timeZone) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const f = fmt(locale, timeZone, { hour: "2-digit", minute: "2-digit", hour12: false });
    return f.format(d).replace(":", "h");
  }

  function formatDateTime(iso, locale, timeZone) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const f = fmt(locale, timeZone, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return `${cap(f.format(d))} \u00e0 ${formatTime(iso, locale, timeZone)}`;
  }

  async function getJson(url) {
    const res = await fetch(url, { method: "GET", credentials: "same-origin" });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && (data.error || data.erreur)) || `HTTP ${res.status}`);
    if (!data) throw new Error("R\u00e9ponse JSON vide");
    if (data.ok === false || data.success === false) throw new Error(data.error || data.erreur || TXT.ctaError);
    return data;
  }

  async function postForm(url, payload) {
    const params = new URLSearchParams();
    Object.entries(payload || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      params.append(k, String(v));
    });
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
      body: params.toString()
    });
    return (await res.json().catch(() => null)) || { ok: false, success: false, error: `HTTP ${res.status}` };
  }

  class CalendarBooking {
    constructor(root) {
      this.root = root;
      this.apiBase = "";
      this.cache = new Map();
      this.rangeData = null;
      this.ui = {};
      this._rangeRequestSeq = 0;
      this._activeRangeRequestSeq = 0;

      this.cfg = {
        locale: root.dataset.locale || "fr-FR",
        tz: root.dataset.tz || "Europe/Paris",
        checkoutUrl: root.dataset.checkoutUrl || "/cart",
        debug: toBool(root.dataset.debug, false),
        maxDays: Math.max(30, toInt(root.dataset.maxMonthsAhead, 3) * 31),
        holdMinutes: Math.max(0, toInt(root.dataset.lockMinutes, 60))
      };

      this.txt = {
        title: root.dataset.titleText || TXT.title,
        pickDate: root.dataset.pickDateText || TXT.pickDate,
        pickSlot: root.dataset.pickSlotText || TXT.pickSlot,
        cta: root.dataset.ctaText || TXT.cta,
        ctaAfter: root.dataset.ctaAfterText || TXT.ctaAfter,
        ctaLoading: root.dataset.ctaLoadingText || TXT.ctaLoading,
        added: root.dataset.addedText || TXT.added,
        lockedNotice: root.dataset.lockedNoticeText || TXT.lockedNotice,
        checkoutAction: root.dataset.checkoutActionText || TXT.checkoutAction,
        error: root.dataset.errorText || TXT.ctaError,
        noSlot: root.dataset.errorNoSlotText || TXT.noSlot
      };
      if (/finaliser ma commande/i.test(this.txt.checkoutAction)) {
        this.txt.checkoutAction = TXT.checkoutAction;
      }
      if (/poursuivre mes achats/i.test(norm(this.txt.checkoutAction))) {
        this.txt.checkoutAction = TXT.checkoutAction;
      }
      const ctaLoadingNorm = norm(this.txt.ctaLoading);
      if (
        !String(this.txt.ctaLoading || "").trim() ||
        ctaLoadingNorm.includes("validation en cours") ||
        ctaLoadingNorm === "validation..."
      ) {
        this.txt.ctaLoading = "";
      }
      const ctaAfterNorm = norm(this.txt.ctaAfter);
      if (
        ctaAfterNorm === norm(this.txt.cta) ||
        ctaAfterNorm.includes("creneau reserve") ||
        ctaAfterNorm.includes("reservation validee")
      ) {
        this.txt.ctaAfter = TXT.ctaAfter;
      }

      const todayKey = dateKeyNow(this.cfg.tz);
      const p = parseDateKey(todayKey);

      this.state = {
        productId: root.dataset.productId || "",
        productTitle: root.dataset.productTitle || "",
        variantId: root.dataset.variantId || "",
        variantTitle: root.dataset.variantTitle || "",
        serviceLookupName: "",
        qty: 1,
        minDateKey: todayKey,
        rangeStartKey: todayKey,
        rangeEndKey: addDays(todayKey, this.cfg.maxDays),
        monthY: p ? p.y : new Date().getFullYear(),
        monthM: p ? p.m - 1 : new Date().getMonth(),
        selectedDateKey: "",
        selectedSlots: [],
        requiredSlots: 1,
        durationMin: 60,
        stepMin: 60,
        allowUnalignedTailSlot: true,
        strictHourly: false,
        capacity: 1,
        delayHours: 0,
        busy: [],
        closedDates: new Set(),
        serviceMessage: "",
        processing: false,
        locked: false
      };

      this.root.classList.remove("is-locked");
      this.init();
    }

    log(...args) {
      if (!this.cfg.debug) return;
      // eslint-disable-next-line no-console
      console.log("[calendar-booking]", ...args);
    }

    beginRangeRequest_() {
      this._rangeRequestSeq += 1;
      this._activeRangeRequestSeq = this._rangeRequestSeq;
      return this._activeRangeRequestSeq;
    }

    isActiveRangeRequest_(seq) {
      if (!seq) return true;
      return this._activeRangeRequestSeq === seq;
    }

    staleRangeRequestError_() {
      const err = new Error("__LFDL_STALE_RANGE_REQUEST__");
      err.code = "LFDL_STALE_RANGE_REQUEST";
      return err;
    }

    readVariantStateFromDom_() {
      const scope = this.root.closest("[data-lfdl-product]") || document;
      const out = { id: "", title: "" };

      const pickers = Array.from(scope.querySelectorAll("variant-selects"));
      for (const picker of pickers) {
        const optionSelects = Array.from(picker.querySelectorAll('select[name^="options["], select'));
        if (!optionSelects.length) continue;
        const selectedOptions = optionSelects.map((select) => String(select.value || ""));

        let variants = [];
        const json = picker.querySelector('script[type="application/json"]');
        if (json && json.textContent) {
          try {
            variants = JSON.parse(json.textContent);
          } catch (err) {
            variants = [];
          }
        }
        if (Array.isArray(variants) && variants.length) {
          const match = variants.find((variant) => {
            const opts = Array.isArray(variant && variant.options) ? variant.options : [];
            if (opts.length !== selectedOptions.length) return false;
            return opts.every((opt, idx) => String(opt) === selectedOptions[idx]);
          });
          if (match && match.id) {
            out.id = String(match.id).trim();
            out.title = String(match.title || "").trim();
            return out;
          }
        }
      }

      const sectionId = String((scope.dataset && scope.dataset.sectionId) || "").trim();
      if (sectionId) {
        const form = scope.querySelector(`#product-form-${sectionId}`);
        const input = form ? form.querySelector('input[name="id"]') : null;
        if (input && input.value) out.id = String(input.value).trim();
      }

      if (!out.id) {
        const hidden = scope.querySelector('input[name="id"].product-variant-id, input[name="id"][type="hidden"], select[name="id"]');
        if (hidden && hidden.value) out.id = String(hidden.value).trim();
      }

      if (!out.title) {
        const selectedOpt = scope.querySelector('select[name="id"] option:checked');
        if (selectedOpt && selectedOpt.textContent) out.title = selectedOpt.textContent.trim();
      }

      if (!out.title) {
        const selectedOpt =
          scope.querySelector('variant-selects select option:checked') ||
          scope.querySelector('.product-form__input--dropdown option:checked') ||
          scope.querySelector('select option:checked');
        if (selectedOpt && selectedOpt.textContent) out.title = String(selectedOpt.textContent || "").trim();
      }

      return out;
    }

    readVariantTitleFromDom_() {
      return String(this.readVariantStateFromDom_().title || "").trim();
    }

    readVariantIdFromDom_() {
      return String(this.readVariantStateFromDom_().id || "").trim();
    }

    currentServiceLabel_() {
      const variantTitle = String(this.state.variantTitle || this.readVariantTitleFromDom_() || "").trim();
      if (variantTitle && !/^default\s*title$/i.test(variantTitle)) return variantTitle;
      return String(this.state.productTitle || "").trim();
    }

    serviceLookupCandidates_() {
      const out = [];
      const push = (value) => {
        const txt = String(value || "").trim();
        if (!txt || /^default\s*title$/i.test(txt)) return;
        if (out.some((entry) => norm(entry) === norm(txt))) return;
        out.push(txt);
      };

      push(this.currentServiceLabel_());
      push(this.state.productTitle || "");
      return out;
    }

    isNoBookingVariant_() {
      const txt = norm(this.currentServiceLabel_());
      if (!txt) return false;
      const isDegagement =
        txt.includes("degagement") ||
        txt.includes("liberation / degagement") ||
        txt.includes("liberation et degagement") ||
        txt.includes("pack (3 ou 6") ||
        txt.includes("pack 3 ou 6");
      const isHarmonisation =
        txt.includes("harmonisation") ||
        txt.includes("purification des lieux") ||
        txt.includes("simple d'un lieu") ||
        txt.includes("simple d un lieu");
      const isLieux =
        txt.includes("lieu") ||
        txt.includes("lieux") ||
        txt.includes("maison") ||
        txt.includes("habitation") ||
        txt.includes("appartement") ||
        txt.includes("local");
      return isHarmonisation && isLieux && !isDegagement;
    }

    isStrictHourlyContext_(service) {
      const svc = service || {};
      const handleNorm = norm(this.root.dataset.productHandle || "");
      const serviceNorm = norm(svc.nom_service || svc.nom || "");
      const categoryNorm = norm(svc.categorie_horaire || svc.categorie || "");
      const titleNorm = norm(this.state.productTitle || "");
      const variantNorm = norm(this.currentServiceLabel_());
      const productIdNorm = String(this.state.productId || "").trim();

      return (
        categoryNorm.includes("liberation") ||
        productIdNorm === "8460839158094" ||
        (handleNorm.includes("liberation") && (handleNorm.includes("degagement") || handleNorm.includes("pack"))) ||
        (titleNorm.includes("liberation") && (titleNorm.includes("degagement") || titleNorm.includes("pack"))) ||
        (variantNorm.includes("liberation") && (variantNorm.includes("degagement") || variantNorm.includes("pack"))) ||
        (serviceNorm.includes("liberation") && (serviceNorm.includes("degagement") || serviceNorm.includes("pack")))
      );
    }

    isHalfHourlyContext_(service) {
      const svc = service || {};
      const handleNorm = norm(this.root.dataset.productHandle || "");
      const categoryNorm = norm(svc.categorie_horaire || svc.categorie || "");
      const titleNorm = norm(this.state.productTitle || "");
      const variantNorm = norm(this.currentServiceLabel_());
      const serviceNorm = norm(svc.nom_service || svc.nom || "");

      return (
        handleNorm.includes("telephone") ||
        handleNorm.includes("telephonique") ||
        categoryNorm.includes("telephone") ||
        categoryNorm.includes("telephonique") ||
        titleNorm.includes("telephone") ||
        titleNorm.includes("telephonique") ||
        variantNorm.includes("telephone") ||
        variantNorm.includes("telephonique") ||
        serviceNorm.includes("telephone") ||
        serviceNorm.includes("telephonique")
      );
    }

    setBookingEnabled_(enabled, message) {
      const on = Boolean(enabled);
      this.root.classList.toggle("is-disabled-service", !on);
      if (this.ui && this.ui.shell) {
        this.ui.shell.hidden = !on;
      }
      if (!on) {
        this.setMessage(message || "Ce service ne propose pas de rendez-vous.", "error");
      } else {
        this.setMessage("", "");
      }
    }

    async init() {
      try {
        this.buildUi();
        this.bindUi();
        this.bindVariantSync();
        const variantState = this.readVariantStateFromDom_();
        this.state.variantId = variantState.id || this.state.variantId;
        this.state.variantTitle = variantState.title || this.state.variantTitle;
        if (this.isNoBookingVariant_()) {
          this.setBookingEnabled_(false, "Ce service ne propose pas de rendez-vous.");
          return;
        }
        this.setBookingEnabled_(true);
        this.updateDateButton();
        this.updateCta();
        const reqSeq = this.beginRangeRequest_();
        await this.resolveApiBase();
        if (!this.isActiveRangeRequest_(reqSeq)) return;
        await this.loadRange(this.state.rangeStartKey, this.state.rangeEndKey, reqSeq);
        if (!this.isActiveRangeRequest_(reqSeq)) return;
        this.renderCalendar();
        if (!this.isActiveRangeRequest_(reqSeq)) return;
        await this.autoSelect();
      } catch (e) {
        if (e && e.code === "LFDL_STALE_RANGE_REQUEST") return;
        this.log(e);
        var msgInit = String((e && e.message) || this.txt.error || "");
        if (/service\s+introuvable|inactif/i.test(msgInit)) {
          this.setBookingEnabled_(false, "Ce service ne propose pas de rendez-vous.");
          return;
        }
        this.state.processing = false;
        this.state.locked = false;
        this.updateCta();
        this.hidePostActions();
        this.ui.wrap.hidden = true;
        this.ui.dateBtn.setAttribute("aria-expanded", "false");
        this.ui.slotsLabel.hidden = true;
        this.setMessage(this.sanitizeUiErrorMessage_(msgInit), "error");
      }
    }

    buildUi() {
      let shell = this.root.querySelector(".calendar-booking__shell");
      if (!shell) {
        shell = document.createElement("div");
        shell.className = "calendar-booking__shell";
        while (this.root.firstChild) shell.appendChild(this.root.firstChild);
        this.root.appendChild(shell);
      }

      let header = shell.querySelector(".calendar-booking__header-group");
      if (!header) {
        header = document.createElement("div");
        header.className = "calendar-booking__header-group";
        shell.insertBefore(header, shell.firstChild);
      }

      let title = header.querySelector(".calendar-booking__title");
      if (!title) {
        title = document.createElement("p");
        title.className = "calendar-booking__title";
        header.insertBefore(title, header.firstChild);
      }
      title.textContent = this.txt.title;

      let dateBtn = header.querySelector(".calendar-booking__display-date");
      if (!dateBtn) {
        dateBtn = document.createElement("button");
        dateBtn.type = "button";
        dateBtn.className = "calendar-booking__display-date";
        header.appendChild(dateBtn);
      }

      let wrap = shell.querySelector(".calendar-booking__calendar-wrapper");
      if (!wrap) {
        wrap = document.createElement("div");
        wrap.className = "calendar-booking__calendar-wrapper";
        wrap.hidden = true;
        const uid = this.root.dataset.productId || Math.random().toString(36).slice(2);
        wrap.id = `calendar-booking-grid-${uid}`;
        wrap.innerHTML = `
          <div class="calendar-booking__nav">
            <button type="button" class="cb-prev" aria-label="Mois precedent">&lt;</button>
            <span class="cb-month-label">...</span>
            <button type="button" class="cb-next" aria-label="Mois suivant">&gt;</button>
          </div>
          <div class="calendar-booking__weekdays" aria-hidden="true">
            <span>Lun</span><span>Mar</span><span>Mer</span><span>Jeu</span><span>Ven</span><span>Sam</span><span>Dim</span>
          </div>
          <div class="calendar-booking__grid" role="listbox" aria-label="Choix de date"></div>
        `;
        shell.appendChild(wrap);
      }

      dateBtn.setAttribute("aria-expanded", "false");
      dateBtn.setAttribute("aria-controls", wrap.id);

      let slotsLabel = shell.querySelector(".calendar-booking__slots-label");
      if (!slotsLabel) {
        slotsLabel = document.createElement("div");
        slotsLabel.className = "calendar-booking__slots-label";
      }
      slotsLabel.textContent = this.txt.pickSlot;

      let slots = shell.querySelector(".calendar-booking__slots");
      if (!slots) {
        slots = document.createElement("div");
        slots.className = "calendar-booking__slots";
        slots.setAttribute("role", "listbox");
        slots.setAttribute("aria-label", "Choix de cr\u00e9neau");
      }

      let notice = shell.querySelector(".calendar-booking__notice");
      if (!notice) {
        notice = document.createElement("div");
        notice.className = "calendar-booking__notice";
        notice.hidden = true;
      }

      let msg = shell.querySelector(".calendar-booking__message");
      if (!msg) {
        msg = document.createElement("div");
        msg.className = "calendar-booking__message";
        msg.setAttribute("role", "status");
        msg.setAttribute("aria-live", "polite");
      }

      let btn = shell.querySelector(".calendar-booking__button");
      if (!btn) {
        btn = document.createElement("button");
        btn.type = "button";
        btn.className = "calendar-booking__button";
        btn.textContent = this.txt.cta;
      }

      let postActions = shell.querySelector(".calendar-booking__post-actions");
      if (!postActions) {
        postActions = document.createElement("div");
        postActions.className = "calendar-booking__post-actions";
        postActions.hidden = true;
        postActions.style.display = "none";
        postActions.innerHTML = `
          <div class="calendar-booking__post-message"></div>
          <div class="calendar-booking__post-buttons">
            <button type="button" class="calendar-booking__post-checkout">${this.txt.checkoutAction}</button>
          </div>
        `;
      }

      let postMessage = postActions.querySelector(".calendar-booking__post-message");
      if (!postMessage) {
        postMessage = document.createElement("div");
        postMessage.className = "calendar-booking__post-message";
        postActions.prepend(postMessage);
      }

      let postButtons = postActions.querySelector(".calendar-booking__post-buttons");
      if (!postButtons) {
        postButtons = document.createElement("div");
        postButtons.className = "calendar-booking__post-buttons";
        postActions.appendChild(postButtons);
      }

      let postCheckout = postActions.querySelector(".calendar-booking__post-checkout");
      if (!postCheckout) {
        postCheckout = document.createElement("button");
        postCheckout.type = "button";
        postCheckout.className = "calendar-booking__post-checkout";
        postButtons.appendChild(postCheckout);
      }
      postCheckout.textContent = this.txt.checkoutAction;
      postCheckout.disabled = true;
      postCheckout.setAttribute("aria-disabled", "true");

      const legacyContinue = postActions.querySelector(".calendar-booking__post-continue");
      if (legacyContinue) legacyContinue.remove();

      shell.appendChild(header);
      shell.appendChild(wrap);
      shell.appendChild(slotsLabel);
      shell.appendChild(slots);
      shell.appendChild(notice);
      shell.appendChild(msg);
      shell.appendChild(btn);
      shell.appendChild(postActions);

      this.ui = {
        shell,
        dateBtn,
        wrap,
        monthLabel: wrap.querySelector(".cb-month-label"),
        prev: wrap.querySelector(".cb-prev"),
        next: wrap.querySelector(".cb-next"),
        grid: wrap.querySelector(".calendar-booking__grid"),
        slotsLabel,
        slots,
        notice,
        msg,
        btn,
        postActions,
        postMessage,
        postCheckout
      };
    }

    bindUi() {
      this.ui.dateBtn.addEventListener("click", () => {
        const open = this.ui.wrap.hidden;
        this.ui.wrap.hidden = !open;
        this.ui.dateBtn.setAttribute("aria-expanded", open ? "true" : "false");
        this.setOpen_(open);
        if (open) this.renderCalendar();
      });

      this.ui.prev?.addEventListener("click", () => {
        const cur = this.state.monthY * 12 + this.state.monthM;
        const min = parseDateKey(this.state.minDateKey);
        const minIx = min ? min.y * 12 + (min.m - 1) : cur;
        if (cur <= minIx) return;
        if (this.state.monthM === 0) {
          this.state.monthM = 11;
          this.state.monthY -= 1;
        } else {
          this.state.monthM -= 1;
        }
        this.renderCalendar();
      });

      this.ui.next?.addEventListener("click", async () => {
        const ny = this.state.monthM === 11 ? this.state.monthY + 1 : this.state.monthY;
        const nm = this.state.monthM === 11 ? 0 : this.state.monthM + 1;
        await this.ensureMonthCovered(ny, nm);
        this.state.monthY = ny;
        this.state.monthM = nm;
        this.renderCalendar();
      });

      this.ui.btn.addEventListener("click", () => this.confirm());
      this.ui.postCheckout?.addEventListener("click", () => {
        window.location.href = this.cfg.checkoutUrl || "/cart";
      });
    }

    setOpen_(open) {
      this.root.classList.toggle("is-open", Boolean(open));
    }

    bindVariantSync() {
      const applyVariant = (variantId, variantTitle) => {
        const currentId = String(this.state.variantId || "").trim();
        const currentTitle = String(this.state.variantTitle || "").trim();
        let next = String(variantId || "").trim();
        const nextTitle = String(variantTitle || this.readVariantTitleFromDom_() || "").trim();
        if (!next) {
          next = String(currentId || this.readVariantIdFromDom_() || "").trim();
        }
        if (!next && !nextTitle) return;
        if (next === currentId && nextTitle === currentTitle) return;
        if (next) {
          this.state.variantId = next;
          this.root.dataset.variantId = next;
        }
        this.state.variantTitle = nextTitle;
        this.root.dataset.variantTitle = nextTitle;
        this.refreshForVariant();
      };

      const bindNativeVariantInputs = () => {
        const scope = this.root.closest("[data-lfdl-product]") || document;
        const variantPickers = Array.from(scope.querySelectorAll("variant-selects"));
        variantPickers.forEach((picker) => {
          if (!picker || picker.dataset.lfdlCbBound === "1") return;
          picker.dataset.lfdlCbBound = "1";
          picker.addEventListener("change", () => {
            const st = this.readVariantStateFromDom_();
            applyVariant(st.id, st.title);
          });
        });

        const optionSelects = Array.from(scope.querySelectorAll('variant-selects select[name^="options["]'));
        optionSelects.forEach((select) => {
          if (!select || select.dataset.lfdlCbBound === "1") return;
          select.dataset.lfdlCbBound = "1";
          select.addEventListener("change", () => {
            const st = this.readVariantStateFromDom_();
            applyVariant(st.id, st.title);
          });
        });

        const selects = Array.from(scope.querySelectorAll('select[name="id"]'));
        selects.forEach((select) => {
          if (!select || select.dataset.lfdlCbBound === "1") return;
          select.dataset.lfdlCbBound = "1";
          select.addEventListener("change", () => {
            const opt = select.options && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
            const title = opt ? String(opt.textContent || "").trim() : "";
            applyVariant(select.value, title);
          });
        });

        const hiddenInputs = Array.from(scope.querySelectorAll('input[name="id"].product-variant-id, input[name="id"][type="hidden"]'));
        hiddenInputs.forEach((input) => {
          if (!input || input.dataset.lfdlCbBound === "1") return;
          input.dataset.lfdlCbBound = "1";
          input.addEventListener("change", () => {
            applyVariant(input.value, this.readVariantTitleFromDom_());
          });
        });
      };

      this.root.addEventListener("lfdl:variant-change", (evt) => {
        const detail = evt && evt.detail ? evt.detail : {};
        const next = detail.variantId || this.root.dataset.variantId;
        const nextTitle = detail.variantTitle || this.root.dataset.variantTitle || "";
        applyVariant(next, nextTitle);
      });

      if (typeof subscribe === "function" && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        const scope = this.root.closest("[data-lfdl-product]") || document;
        const sectionId = String((scope.dataset && scope.dataset.sectionId) || "").trim();
        subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data) return;
          const eventSectionId = String(event.data.sectionId || "").trim();
          if (sectionId && eventSectionId && sectionId !== eventSectionId) return;
          const variant = event.data.variant || {};
          applyVariant(variant.id || "", variant.title || "");
        });
      }

      if (typeof MutationObserver === "function") {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === "attributes" && mutation.attributeName === "data-variant-id") {
              applyVariant(this.root.dataset.variantId, this.root.dataset.variantTitle || this.readVariantTitleFromDom_());
              break;
            }
          }
        });
        observer.observe(this.root, { attributes: true, attributeFilter: ["data-variant-id", "data-variant-title"] });
      }

      bindNativeVariantInputs();
      const st = this.readVariantStateFromDom_();
      applyVariant(st.id, st.title);
    }

    async refreshForVariant() {
      const reqSeq = this.beginRangeRequest_();
      this.state.processing = false;
      this.state.locked = false;
      this.state.serviceLookupName = "";
      this.state.selectedDateKey = "";
      this.state.selectedSlots = [];
      this.hidePostActions();
      this.updateDateButton();
      this.updateCta();
      this.setMessage("", "");
      this.ui.wrap.hidden = true;
      this.ui.dateBtn.setAttribute("aria-expanded", "false");
      this.ui.slotsLabel.hidden = true;

      if (this.isNoBookingVariant_()) {
        this.setBookingEnabled_(false, "Ce service ne propose pas de rendez-vous.");
        return;
      }
      this.setBookingEnabled_(true);

      try {
        await this.loadRange(this.state.rangeStartKey, this.state.rangeEndKey, reqSeq);
        if (!this.isActiveRangeRequest_(reqSeq)) return;
        const min = parseDateKey(this.state.minDateKey);
        if (min) {
          this.state.monthY = min.y;
          this.state.monthM = min.m - 1;
        }
        this.renderCalendar();
        if (!this.isActiveRangeRequest_(reqSeq)) return;
        await this.autoSelect();
      } catch (e) {
        if (e && e.code === "LFDL_STALE_RANGE_REQUEST") return;
        this.log(e);
        var msgRefresh = String((e && e.message) || this.txt.error || "");
        if (/service\s+introuvable|inactif/i.test(msgRefresh)) {
          this.setBookingEnabled_(false, "Ce service ne propose pas de rendez-vous.");
          return;
        }
        this.setMessage(this.sanitizeUiErrorMessage_(msgRefresh), "error");
      }
    }

    async resolveApiBase() {
      const direct = String(this.root.dataset.calendarBaseUrl || "").trim();
      if (direct && /^https?:\/\//i.test(direct)) {
        this.apiBase = direct;
        return;
      }
      const cfgUrl = String(this.root.dataset.calendarConfigUrl || "").trim();
      if (cfgUrl) {
        try {
          const cfg = await getJson(cfgUrl);
          const fromFile = String(cfg.baseUrl || "").trim();
          if (fromFile && /^https?:\/\//i.test(fromFile)) {
            this.apiBase = fromFile;
            return;
          }
        } catch (err) {
          this.log("calendar-config fetch failed", err);
        }
      }
      throw new Error("URL calendrier manquante.");
    }

    selectedQty() {
      const scopes = [
        this.root.closest("form"),
        this.root,
        this.root.closest(".shopify-section")
      ];
      for (const scope of scopes) {
        if (!scope || typeof scope.querySelector !== "function") continue;
        const input = scope.querySelector('input[name="quantity"], input.quantity__input, .quantity__input input, input[id*="Quantity"]');
        if (!input) continue;
        const qty = Math.max(1, toInt(input.value, 1));
        if (qty > 0) return qty;
      }
      return 1;
    }

    async ensureMonthCovered(y, m0) {
      const days = new Date(Date.UTC(y, m0 + 1, 0, 12, 0, 0, 0)).getUTCDate();
      const target = dateKey(y, m0 + 1, days);
      if (cmpDateKey(target, this.state.rangeEndKey) <= 0) return;
      const maxEnd = addDays(this.state.minDateKey, this.cfg.maxDays);
      const effective = cmpDateKey(target, maxEnd) < 0 ? target : maxEnd;
      await this.loadRange(this.state.rangeStartKey, effective);
    }

    rangeUrl(startKey, endKey, serviceNameOverride) {
      const qty = this.selectedQty();
      const p = new URLSearchParams();
      const variantLabel = String(this.currentServiceLabel_() || "");
      const productTitle = String(this.state.productTitle || "").trim();
      const baseServiceName = String(serviceNameOverride || "").trim() || variantLabel || productTitle;
      p.set("action", "availability");
      p.set("productId", String(this.state.productId || ""));
      p.set("variantId", String(this.state.variantId || ""));
      p.set("variantTitle", variantLabel);
      p.set("service_name", baseServiceName);
      p.set("service_product_name", productTitle);
      p.set("service_variant_name", variantLabel);
      p.set("qty", String(qty));
      p.set("start", `${startKey}T00:00:00`);
      p.set("end", `${endKey}T23:59:59`);
      return `${this.apiBase}?${p.toString()}`;
    }

    async loadRange(startKey, endKey, reqSeq) {
      if (!this.state.productId) throw new Error("Product ID manquant.");
      if (!this.isActiveRangeRequest_(reqSeq)) throw this.staleRangeRequestError_();
      const candidates = this.serviceLookupCandidates_();
      const lookupList = candidates.length ? candidates : [String(this.state.productTitle || "").trim()].filter(Boolean);
      let data = null;
      let lastErr = null;

      for (let i = 0; i < lookupList.length; i += 1) {
        if (!this.isActiveRangeRequest_(reqSeq)) throw this.staleRangeRequestError_();
        const candidate = String(lookupList[i] || "").trim();
        try {
          data = await getJson(this.rangeUrl(startKey, endKey, candidate));
          if (!this.isActiveRangeRequest_(reqSeq)) throw this.staleRangeRequestError_();
          this.state.serviceLookupName = candidate;
          break;
        } catch (err) {
          if (err && err.code === "LFDL_STALE_RANGE_REQUEST") throw err;
          if (!this.isActiveRangeRequest_(reqSeq)) throw this.staleRangeRequestError_();
          lastErr = err;
          const msg = String((err && err.message) || "");
          const retryable = /service\s+introuvable|inactif/i.test(msg);
          if (!retryable || i === lookupList.length - 1) {
            throw err;
          }
        }
      }

      if (!data) {
        throw lastErr || new Error("Impossible de recuperer les disponibilites.");
      }
      if (!this.isActiveRangeRequest_(reqSeq)) throw this.staleRangeRequestError_();
      this.rangeData = data;
      this.state.rangeStartKey = startKey;
      this.state.rangeEndKey = endKey;

      const svc = data.service || {};
      this.state.requiredSlots = Math.min(12, Math.max(1, toInt(svc.nb_seances, 1)));
      this.state.durationMin = Math.min(360, Math.max(15, toInt(svc.duree_minutes || svc.duree, 60)));
      this.state.stepMin = Math.min(360, Math.max(5, toInt(svc.slot_step_minutes || svc.pas_minutes, this.state.durationMin)));
      const isHalfHourlyService = this.isHalfHourlyContext_(svc);

      if (isHalfHourlyService) {
        this.state.stepMin = 30;
        this.state.allowUnalignedTailSlot = true;
        this.state.strictHourly = false;
      } else {
        this.state.stepMin = Math.max(60, this.state.stepMin);
        this.state.allowUnalignedTailSlot = false;
        this.state.strictHourly = true;
      }
      this.state.capacity = Math.min(20, Math.max(1, toInt(svc.capacite, 1)));
      this.state.delayHours = Math.max(0, toInt(svc.delai_min_heures ?? svc.delai_min_heure ?? svc.delai_min_hours, 0));
      this.state.serviceMessage = String(svc.message_client || "").trim();

      const vis = toInt(svc.visibilite_jours, 0);
      if (vis > this.cfg.maxDays) {
        this.cfg.maxDays = Math.min(365, vis);
      }

      this.state.busy = this.parseBusy(data.busy || []);
      this.state.closedDates = this.parseClosedDates(data);
      this.buildCache();
      this.renderNotice();
    }

    parseClosedDates(data) {
      const out = new Set();
      const candidates = []
        .concat(data?.closed || [])
        .concat(data?.closed_dates || [])
        .concat(data?.fermes || [])
        .concat(data?.jours_fermes || [])
        .concat(data?.complets || []);
      (Array.isArray(candidates) ? candidates : []).forEach((entry) => {
        if (!entry) return;
        if (typeof entry === "string") {
          const raw = entry.trim();
          const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
          if (m) out.add(m[1]);
          return;
        }
        if (typeof entry === "object") {
          const dateRaw = String(entry.date || entry.day || entry.date_key || entry.jour || "").trim();
          const m = dateRaw.match(/^(\d{4}-\d{2}-\d{2})/);
          if (m) out.add(m[1]);
        }
      });
      return out;
    }

    parseBusy(list) {
      const out = [];
      const fallbackMs = this.state.durationMin * 60000;

      const push = (startRaw, endRaw, countRaw) => {
        const s = Date.parse(String(startRaw || ""));
        let e = Date.parse(String(endRaw || ""));
        if (!Number.isFinite(s)) return;
        if (!Number.isFinite(e) || e <= s) e = s + fallbackMs;
        out.push({ s, e, n: Math.max(1, toInt(countRaw, 1)) });
      };

      const parseDates = (v) => {
        if (!v) return [];
        if (Array.isArray(v)) return v;
        if (typeof v === "string") {
          const raw = v.trim();
          if (!raw) return [];
          try {
            const j = JSON.parse(raw);
            if (Array.isArray(j)) return j;
          } catch (_e) {
            // noop
          }
          if (raw.includes("|")) return raw.split("|").map((x) => x.trim()).filter(Boolean);
          return [raw];
        }
        return [];
      };

      const startKeys = ["start", "slot_start_iso", "slot_start", "debut", "date_start", "start_iso"];
      const endKeys = ["end", "slot_end_iso", "slot_end", "fin", "date_end", "end_iso"];

      (Array.isArray(list) ? list : []).forEach((it) => {
        if (!it) return;
        if (typeof it === "string") {
          push(it, "", 1);
          return;
        }
        if (Array.isArray(it)) {
          it.forEach((x) => push(x, "", 1));
          return;
        }
        if (typeof it === "object") {
          const count = it.count ?? it.qty ?? it.capacity ?? 1;
          let s = "";
          let e = "";
          for (const k of startKeys) {
            if (it[k]) {
              s = it[k];
              break;
            }
          }
          for (const k of endKeys) {
            if (it[k]) {
              e = it[k];
              break;
            }
          }
          if (s) {
            push(s, e, count);
            return;
          }
          const entries = parseDates(it.dates ?? it.slots ?? it.slot);
          entries.forEach((entry) => {
            if (typeof entry === "object" && entry) {
              push(entry.start || entry.slot_start_iso || entry.slot_start, entry.end || entry.slot_end_iso || entry.slot_end, count);
            } else {
              push(entry, "", count);
            }
          });
        }
      });

      out.sort((a, b) => a.s - b.s);
      return out;
    }

    overlaps(s, e) {
      let n = 0;
      this.state.busy.forEach((b) => {
        if (b.s < e && b.e > s) n += b.n;
      });
      return n;
    }

    buildCache() {
      this.cache.clear();
      let cur = this.state.rangeStartKey;
      while (cmpDateKey(cur, this.state.rangeEndKey) <= 0) {
        this.cache.set(cur, this.computeSlots(cur));
        cur = addDays(cur, 1);
      }
    }

    computeSlots(dateK) {
      if (cmpDateKey(dateK, this.state.minDateKey) < 0) return [];
      if (this.state.closedDates && this.state.closedDates.has(dateK)) return [];

      const horaires = this.rangeData?.horaires || {};
      const dk = norm(dayKeyForDate(dateK, this.cfg.tz));
      const plages = Object.entries(horaires).reduce((acc, [k, v]) => {
        if (acc) return acc;
        if (norm(k) === dk && Array.isArray(v)) return v;
        return acc;
      }, null) || [];

      if (!plages.length) return [];

      const durationMs = this.state.durationMin * 60000;
      const stepMs = this.state.stepMin * 60000;
      const cutoffMs = Date.now() + this.state.delayHours * 3600000;
      const strictHourly = this.state.strictHourly || this.isStrictHourlyContext_(this.rangeData?.service || {});
      const slots = [];

      plages.forEach((p) => {
        const startMs = tzDateTimeToUtcMs(dateK, p?.start, this.cfg.tz);
        const endMs = tzDateTimeToUtcMs(dateK, p?.end, this.cfg.tz);
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return;

        for (let s = startMs; s + durationMs <= endMs; s += stepMs) {
          const e = s + durationMs;
          if (s < cutoffMs) continue;
          if (strictHourly && tzParts(new Date(s), this.cfg.tz).min !== 0) continue;
          if (this.overlaps(s, e) >= this.state.capacity) continue;
          slots.push({ startMs: s, endMs: e, startIso: new Date(s).toISOString(), endIso: new Date(e).toISOString() });

}

        if (this.state.allowUnalignedTailSlot && !strictHourly) {
          // Si la fermeture n'est pas alignee sur le step (ex: 21:30 avec step 120),
          // on peut ajouter le dernier creneau possible (fermeture - duree).
          const lastStart = endMs - durationMs;
          if (Number.isFinite(lastStart) && lastStart >= startMs && lastStart >= cutoffMs) {
            const lastEnd = lastStart + durationMs;
            if (this.overlaps(lastStart, lastEnd) < this.state.capacity) {
              const exists = slots.some((x) => x.startMs === lastStart);
              if (!exists) {
                slots.push({
                  startMs: lastStart,
                  endMs: lastEnd,
                  startIso: new Date(lastStart).toISOString(),
                  endIso: new Date(lastEnd).toISOString()
                });
              }
            }
          }
        }
      });

      if (strictHourly) {
        return slots
          .filter((slot) => tzParts(new Date(slot.startMs), this.cfg.tz).min === 0)
          .sort((a, b) => a.startMs - b.startMs);
      }

      slots.sort((a, b) => a.startMs - b.startMs);
      return slots;
    }

    renderNotice() {
      if (!this.state.serviceMessage) {
        this.ui.notice.hidden = true;
        this.ui.notice.textContent = "";
        return;
      }
      this.ui.notice.hidden = false;
      this.ui.notice.textContent = this.state.serviceMessage;
    }

    renderCalendar() {
      const y = this.state.monthY;
      const m0 = this.state.monthM;
      const monthRef = new Date(Date.UTC(y, m0, 1, 12, 0, 0, 0));
      this.ui.monthLabel.textContent = cap(fmt(this.cfg.locale, this.cfg.tz, { month: "long", year: "numeric" }).format(monthRef));

      const firstK = dateKey(y, m0 + 1, 1);
      const wd = weekdayIndex(firstK, this.cfg.tz);
      const offset = wd === 0 ? 6 : wd - 1;
      const days = new Date(Date.UTC(y, m0 + 1, 0, 12, 0, 0, 0)).getUTCDate();

      const frag = document.createDocumentFragment();
      for (let i = 0; i < offset; i += 1) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "cb-day cb-day--blank";
        b.disabled = true;
        b.setAttribute("aria-hidden", "true");
        frag.appendChild(b);
      }

      for (let d = 1; d <= days; d += 1) {
        const k = dateKey(y, m0 + 1, d);
        const b = document.createElement("button");
        b.type = "button";
        b.className = "cb-day";
        b.textContent = String(d);

        const inRange = cmpDateKey(k, this.state.rangeStartKey) >= 0 && cmpDateKey(k, this.state.rangeEndKey) <= 0;
        const past = cmpDateKey(k, this.state.minDateKey) < 0;
        const av = inRange && (this.cache.get(k) || []).length > 0;

        if (k === this.state.minDateKey) b.classList.add("cb-day--today");
        if (k === this.state.selectedDateKey) {
          b.classList.add("cb-day--selected");
          b.setAttribute("aria-selected", "true");
        } else {
          b.setAttribute("aria-selected", "false");
        }

        if (past || !inRange || !av) {
          b.classList.add("cb-day--disabled");
          b.disabled = true;
        } else {
          b.classList.add("cb-day--available");
          b.addEventListener("click", () => this.selectDate(k, true));
        }

        frag.appendChild(b);
      }

      this.ui.grid.innerHTML = "";
      this.ui.grid.appendChild(frag);
      this.updateMonthButtons();
    }

    updateMonthButtons() {
      const min = parseDateKey(this.state.minDateKey);
      const max = parseDateKey(this.state.rangeEndKey);
      if (!min || !max) return;
      const cur = this.state.monthY * 12 + this.state.monthM;
      const minIx = min.y * 12 + (min.m - 1);
      const maxIx = max.y * 12 + (max.m - 1);
      if (this.ui.prev) this.ui.prev.disabled = cur <= minIx;
      if (this.ui.next) this.ui.next.disabled = cur >= maxIx;
    }

    async autoSelect() {
      let cur = this.state.minDateKey;
      while (cmpDateKey(cur, this.state.rangeEndKey) <= 0) {
        if ((this.cache.get(cur) || []).length > 0) {
          this.selectDate(cur, false);
          this.setMessage("", "");
          return;
        }
        cur = addDays(cur, 1);
      }
      this.state.selectedDateKey = "";
      this.state.selectedSlots = [];
      this.state.locked = false;
      this.renderSlots();
      this.updateDateButton();
      this.updateCta();
      this.setMessage(this.txt.noSlot, "error");
    }

    selectDate(dateK, closeCalendar) {
      this.state.selectedDateKey = dateK;
      if (this.state.requiredSlots === 1) this.state.selectedSlots = [];
      this.state.locked = false;
      this.hidePostActions();
      this.renderCalendar();
      this.renderSlots();
      this.updateDateButton();
      this.updateCta();
      if (closeCalendar) {
        this.ui.wrap.hidden = true;
        this.ui.dateBtn.setAttribute("aria-expanded", "false");
      }
    }

    renderSlots() {
      const dateK = this.state.selectedDateKey;
      const slots = dateK ? (this.cache.get(dateK) || []) : [];
      this.ui.slots.innerHTML = "";
      this.ui.slotsLabel.hidden = !dateK || !slots.length;

      if (this.state.requiredSlots > 1) {
        const info = document.createElement("div");
        info.className = "calendar-booking__pack-info";
        info.textContent = `Pack: ${this.state.selectedSlots.length}/${this.state.requiredSlots} s\u00e9ance(s) s\u00e9lectionn\u00e9e(s).`;
        this.ui.slots.appendChild(info);
      }

      if (!dateK) {
        this.setMessage("", "");
        return;
      }

      if (!slots.length) {
        this.setMessage(this.txt.noSlot, "error");
        return;
      }

      this.setMessage("", "");

      slots.forEach((slot) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "calendar-booking__slot";
        b.textContent = formatTime(slot.startIso, this.cfg.locale, this.cfg.tz);
        if (this.state.selectedSlots.some((x) => x.startIso === slot.startIso)) b.classList.add("is-selected");
        b.addEventListener("click", () => this.toggleSlot(slot));
        this.ui.slots.appendChild(b);
      });
    }

    toggleSlot(slot) {
      const idx = this.state.selectedSlots.findIndex((x) => x.startIso === slot.startIso);
      if (idx >= 0) {
        this.state.selectedSlots.splice(idx, 1);
      } else if (this.state.requiredSlots === 1) {
        this.state.selectedSlots = [slot];
      } else if (this.state.selectedSlots.length < this.state.requiredSlots) {
        this.state.selectedSlots.push(slot);
      } else {
        this.setMessage(`Maximum ${this.state.requiredSlots} s\u00e9ance(s).`, "error");
        return;
      }
      this.state.selectedSlots.sort((a, b) => a.startMs - b.startMs);
      this.state.locked = false;
      this.hidePostActions();
      this.renderSlots();
      this.updateDateButton();
      this.updateCta();
    }

    updateDateButton() {
      let label = this.txt.pickDate;
      if (this.state.selectedDateKey) label = formatDateLong(this.state.selectedDateKey, this.cfg.locale, this.cfg.tz);
      if (this.state.selectedSlots.length === 1) {
        label = formatDateTime(this.state.selectedSlots[0].startIso, this.cfg.locale, this.cfg.tz);
      } else if (this.state.selectedSlots.length > 1) {
        const last = this.state.selectedSlots[this.state.selectedSlots.length - 1];
        label = `${this.state.selectedSlots.length}/${this.state.requiredSlots} s\u00e9ances - ${formatDateTime(last.startIso, this.cfg.locale, this.cfg.tz)}`;
      }
      this.ui.dateBtn.textContent = label;
    }

    updateCta() {
      if (this.state.processing) {
        this.ui.btn.hidden = false;
        this.ui.btn.disabled = true;
        this.ui.btn.textContent = this.processingStatusText_();
        return;
      }
      if (this.state.locked) {
        this.ui.btn.hidden = true;
        this.ui.btn.disabled = true;
        return;
      }
      this.ui.btn.hidden = false;
      const n = this.state.selectedSlots.length;
      const req = this.state.requiredSlots;
      if (n === 0) {
        this.ui.btn.disabled = true;
        this.ui.btn.textContent = this.txt.ctaAfter;
      } else if (n < req) {
        this.ui.btn.disabled = true;
        this.ui.btn.textContent = `Encore ${req - n} s\u00e9ance(s)`;
      } else {
        this.ui.btn.disabled = false;
        this.ui.btn.textContent = this.txt.ctaAfter;
      }
    }

    setMessage(text, type) {
      this.ui.msg.textContent = text || "";
      this.ui.msg.classList.remove("is-error", "is-success");
      if (type === "error") this.ui.msg.classList.add("is-error");
      if (type === "success") this.ui.msg.classList.add("is-success");
    }

    pendingReservationMessage_() {
      const minutes = Math.max(0, toInt(this.cfg.holdMinutes, 60));
      const noticeTpl = String(this.txt.lockedNotice || "").trim();
      const notice = noticeTpl.includes("{minutes}")
        ? noticeTpl.replace(/\{minutes\}/g, String(minutes))
        : noticeTpl || `Il vous est r\u00e9serv\u00e9 pendant ${minutes} minutes jusqu'au paiement.`;
      return [String(this.txt.added || "").trim(), notice].filter(Boolean).join(" ");
    }

    processingStatusText_() {
      const custom = String(this.txt.ctaLoading || "").trim();
      if (custom) return custom;
      return this.pendingReservationMessage_() || TXT.ctaLoading;
    }

    sanitizeUiErrorMessage_(raw) {
      const msg = String(raw || "").trim();
      if (!msg) return this.txt.error || TXT.ctaError;
      const msgNorm = norm(msg);
      if (
        msgNorm.includes("reponse json vide") ||
        msgNorm.includes("json non valide") ||
        msgNorm.includes("json invalide") ||
        msgNorm.includes("unexpected token") ||
        msgNorm.includes("failed to fetch") ||
        msgNorm.includes("url calendrier manquante") ||
        /^http\s*\d+/.test(msgNorm)
      ) {
        return "Impossible de recuperer les disponibilites pour le moment. Reessayez dans quelques instants.";
      }
      return msg;
    }

    hidePostActions() {
      if (!this.ui.postActions) return;
      this.root.classList.remove("is-locked");
      if (this.ui.postCheckout) {
        this.ui.postCheckout.disabled = true;
        this.ui.postCheckout.setAttribute("aria-disabled", "true");
      }
      this.ui.postActions.hidden = true;
      this.ui.postActions.style.display = "none";
    }

    showPostActions() {
      if (!this.ui.postActions) return;
      this.root.classList.add("is-locked");
      if (this.ui.postMessage) {
        this.ui.postMessage.textContent = this.pendingReservationMessage_();
      }
      if (this.ui.postCheckout) {
        this.ui.postCheckout.textContent = this.txt.checkoutAction;
        this.ui.postCheckout.disabled = false;
        this.ui.postCheckout.setAttribute("aria-disabled", "false");
      }
      this.ui.postActions.hidden = false;
      this.ui.postActions.style.display = "grid";
    }

    summaryText() {
      return this.state.selectedSlots
        .map((slot) => formatDateTime(slot.startIso, this.cfg.locale, this.cfg.tz))
        .join(" | ");
    }

    async tryHold() {
      if (!this.apiBase || !this.state.productId) {
        return { holdId: "", holdExpiresAt: "", err: "Configuration API manquante." };
      }
      if (!Array.isArray(this.state.selectedSlots) || this.state.selectedSlots.length === 0) {
        return { holdId: "", holdExpiresAt: "", err: this.txt.pickSlot };
      }
      const firstSlot = String(this.state.selectedSlots[0]?.startIso || "").trim();
      if (!firstSlot) return { holdId: "", holdExpiresAt: "", err: this.txt.pickSlot };
      const qty = this.selectedQty();
      this.state.qty = qty;
      const payload = {
        action: "hold",
        productId: String(this.state.productId),
        productTitle: String(this.state.productTitle || ""),
        service_name: String(this.state.serviceLookupName || this.currentServiceLabel_() || this.state.productTitle || ""),
        service_product_name: String(this.state.productTitle || ""),
        service_title: String(this.currentServiceLabel_() || ""),
        service_variant_name: String(this.currentServiceLabel_() || ""),
        variantId: String(this.state.variantId || ""),
        qty,
        slot: firstSlot,
        selected_slot: firstSlot,
        source: "Shopify",
        slot_count: this.state.selectedSlots.length,
        slots: this.state.selectedSlots.map((s) => s.startIso).join("|"),
        slots_flat: this.state.selectedSlots.map((s) => s.startIso).join("|")
      };
      this.state.selectedSlots.forEach((s, i) => {
        payload[`slot_${i + 1}`] = s.startIso;
      });
      const res = await postForm(this.apiBase, payload);
      const holdId = String(res?.hold_id || res?.holdId || "").trim();
      const holdExpiresAt = String(
        res?.hold_until || res?.holdUntil || res?.hold_expires_at || res?.holdExpiresAt || res?.expires_at || res?.expiresAt || ""
      ).trim();
      const err = String(res?.error || res?.erreur || "").trim();
      return { holdId, holdExpiresAt, err };
    }

    resolveHoldExpiry(holdExpiresAtRaw) {
      const parsedTs = Date.parse(String(holdExpiresAtRaw || "").trim());
      if (Number.isFinite(parsedTs) && parsedTs > Date.now()) {
        return new Date(parsedTs).toISOString();
      }
      const fallbackTs = Date.now() + Math.max(1, this.cfg.holdMinutes) * 60000;
      return new Date(fallbackTs).toISOString();
    }

    async cartUpdateAttrs(holdId, holdExpiresAt) {
      const attributes = {
        booking_product_id: String(this.state.productId || ""),
        booking_service: String(this.currentServiceLabel_() || ""),
        booking_dates: this.summaryText(),
        booking_timezone: this.cfg.tz,
        booking_hold_expires_at: holdExpiresAt || "",
        booking_hold_minutes: String(Math.max(1, this.cfg.holdMinutes))
      };
      if (holdId) attributes.booking_hold_id = String(holdId);
      await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes })
      });
    }

    async cartAdd(holdId, holdExpiresAt) {
      const variantId = toInt(this.state.variantId, 0);
      if (!variantId) throw new Error("Variant ID manquant.");
      const qty = Math.max(1, toInt(this.state.qty, 1));

      const properties = {
        "Date de reservation": this.summaryText(),
        Service: String(this.state.productTitle || ""),
        _booking_signature: "calendar",
        _booking_slots_utc: this.state.selectedSlots.map((s) => s.startIso).join("|"),
        _booking_timezone: this.cfg.tz,
        _booking_product_id: String(this.state.productId || ""),
        _booking_hold_expires_at: holdExpiresAt || "",
        _booking_hold_minutes: String(Math.max(1, this.cfg.holdMinutes))
      };
      if (holdId) properties._HoldID = holdId;

      const res = await fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variantId, quantity: qty, properties })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.description || err?.message || `HTTP ${res.status}`);
      }
      await res.json().catch(() => ({}));
    }

    async confirm() {
      if (this.state.processing) return;
      if (this.state.selectedSlots.length < this.state.requiredSlots) {
        this.setMessage(this.txt.pickSlot, "error");
        return;
      }

      this.hidePostActions();
      this.state.processing = true;
      this.updateCta();
      this.setMessage("", "");

      try {
        const hold = await this.tryHold();
        if (hold.err) throw new Error(hold.err);
        const holdExpiresAt = this.resolveHoldExpiry(hold.holdExpiresAt);
        await this.cartAdd(hold.holdId, holdExpiresAt);
        try {
          await this.cartUpdateAttrs(hold.holdId, holdExpiresAt);
        } catch (attrErr) {
          this.log(attrErr);
        }
        this.state.processing = false;
        this.state.locked = true;
        this.updateCta();
        this.setMessage("", "");
        this.showPostActions();
      } catch (e) {
        this.log(e);
        this.state.processing = false;
        this.state.locked = false;
        this.updateCta();
        this.setMessage(this.sanitizeUiErrorMessage_((e && e.message) || this.txt.error), "error");
      }
    }
  }

  ready(() => {
    document.querySelectorAll(".calendar-booking").forEach((root) => {
      if (root.dataset.calendarReady === "1") return;
      root.dataset.calendarReady = "1";
      // eslint-disable-next-line no-new
      new CalendarBooking(root);
    });
  });
})();
